import { agent } from 'helpers/agent';
import { EChargeType } from 'interfaces/IEditProduct';
import React, { useState, useContext, createContext } from 'react';
import { toast } from 'react-toastify';
import { API_MYEDUZZ } from 'settings';
import Product from 'v2/components/Pages/Product/Edit/classes/Product';

import { Affiliation, AffiliateInstructions, Recruiter, Materials } from './classes/Affiliates';
import { Basic, Details, SupportRefund } from './classes/General';
import { Payments, Subscription, Checkout, Recovery } from './classes/Payment';

const editProductContext = createContext(null);

export function ProvideEditProduct({ children }: any) {
  const editProduct = useProvideEditProduct();
  return <editProductContext.Provider value={editProduct}>{children}</editProductContext.Provider>;
}

export const useEditProduct = () => {
  return useContext(editProductContext);
};

// Provider hook that creates auth object and handles state
function useProvideEditProduct() {
  const [product, setProduct] = useState<Product>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //BASIC
  const [basic, setBasic] = useState<Basic>();
  const [isLoadingBasic, setLoadingBasic] = useState<boolean>(false);
  //DETAILS
  const [details, setDetails] = useState<Details>();
  const [isLoadingDetails, setLoadingDetails] = useState<boolean>(false);
  //SUPPORT REFUND
  const [supportRefund, setSupportRefund] = useState<SupportRefund>();
  const [isLoadingSupportRefund, setLoadingSupportRefund] = useState<boolean>(false);

  //paymentType
  const [paymentType, setPaymentType] = useState<Payments>();
  const [isLoadingPaymentType, setIsLoadingPaymentType] = useState<boolean>(false);

  //checkout
  const [checkout, setCheckout] = useState<Checkout>();
  const [isLoadingCheckout, setIsLoadingCheckout] = useState<boolean>(false);

  //recovery
  const [recovery, setRecovery] = useState<Recovery>();
  const [isLoadingRecovery, setIsLoadingRecovery] = useState<boolean>(false);

  //affiliation
  const [affiliation, setAffiliation] = useState<Affiliation>();
  const [isLoadingAffiliation, setIsLoadingAffiliation] = useState<boolean>(false);

  //affiliateinstructions
  const [affiliateInstructions, setAffiliateInstructions] = useState<AffiliateInstructions>();
  const [isLoadingAffiliateInstructions, setIsLoadingAffiliateInstructions] = useState<boolean>(false);

  //recruiter
  const [recruiter, setRecruiter] = useState<Recruiter>();
  const [isLoadingRecruiter, setIsLoadingRecruiter] = useState<boolean>(false);

  //materials
  const [materials, setMaterials] = useState<Materials>();
  const [isLoadingMaterials, setIsLoadingMaterials] = useState<boolean>(false);

  const setContext = (data: Partial<Product>) => {
    const prod = new Product({ ...product, ...data } as Product);
    setProduct(prod);
    setBasic(new Basic(prod));
    setDetails(new Details(prod));
    setSupportRefund(new SupportRefund(prod));
    if (EChargeType.SUBSCRIPTION !== prod.charge_type) {
      setPaymentType(new Payments(prod));
    }
    if (EChargeType.SUBSCRIPTION === prod.charge_type) {
      setPaymentType(new Subscription(prod));
    }
    setCheckout(new Checkout(prod));
    setRecovery(new Recovery(prod));
    setAffiliation(new Affiliation(prod));
    setAffiliateInstructions(new AffiliateInstructions(prod));
    setRecruiter(new Recruiter(prod));
    setMaterials(new Materials(prod));
  };

  const loadProduct = (id: number): void => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const result = await agent.get(`${API_MYEDUZZ}/newproduct/${id}`);
        const prod: Product = new Product(result.data.data);
        setContext(prod);

        setIsLoading(false);
      } catch (error) {
        toast.error(error.message);
        setIsLoading(false);
      }
    };
    fetchData();
  };

  const saveBasic = (formData: any): void => {
    const fetchData = async () => {
      try {
        setLoadingBasic(true);
        await agent.put(`${API_MYEDUZZ}/newproduct/${product.id}/general/basic`, formData);
        toast.success('Dados básicos do produto salvo com sucesso!');
        setContext(formData);
        setLoadingBasic(false);
      } catch (error) {
        toast.error(error.data.message);
        setLoadingBasic(false);
      }
    };
    fetchData();
  };

  const saveDetails = (formData: any): void => {
    const fetchData = async () => {
      try {
        setLoadingDetails(true);
        await agent.put(`${API_MYEDUZZ}/newproduct/${product.id}/general/detail`, formData);
        toast.success('Detalhes do produto salvo com sucesso!');
        setContext(formData);
        setLoadingDetails(false);
      } catch (error) {
        toast.error(error.data.message);
        setLoadingDetails(false);
      }
    };
    fetchData();
  };

  const saveSupportRefund = (formData: any): void => {
    const fetchData = async () => {
      try {
        setLoadingSupportRefund(true);
        await agent.put(`${API_MYEDUZZ}/newproduct/${product.id}/general/supportrefund`, formData);
        toast.success('Suporte e reembolsos salvo com sucesso!');
        setContext(formData);
        setLoadingSupportRefund(false);
      } catch (error) {
        toast.error(error.data.message);
        setLoadingSupportRefund(false);
      }
    };
    fetchData();
  };

  const savePaymentType = (formData: any): void => {
    const fetchData = async () => {
      try {
        setIsLoadingPaymentType(true);
        await agent.put(`${API_MYEDUZZ}/newproduct/${product.id}/charge/method`, {
          ...formData,
          currency: formData.currency.id,
        });
        toast.success('Formas de cobrança do produto salvo com sucesso!');
        setContext(formData);
        setIsLoadingPaymentType(false);
      } catch (error) {
        toast.error(error.data.message);
        setIsLoadingPaymentType(false);
      }
    };
    fetchData();
  };

  const saveCheckout = (formData: any): void => {
    const fetchData = async () => {
      try {
        setIsLoadingCheckout(true);
        await agent.put(`${API_MYEDUZZ}/newproduct/${product.id}/charge/payment`, formData);
        toast.success('Meios de pagamento e checkout do produto salvo com sucesso!');
        setContext(formData);
        setIsLoadingCheckout(false);
      } catch (error) {
        toast.error(error.data.message);
        setIsLoadingCheckout(false);
      }
    };
    fetchData();
  };

  const saveRecovery = (formData: any): void => {
    const fetchData = async () => {
      try {
        setIsLoadingRecovery(true);
        await agent.put(`${API_MYEDUZZ}/newproduct/${product.id}/charge/recovery`, formData);
        toast.success('Recuperação de vendas do produto salvo com sucesso!');
        setContext(formData);
        setIsLoadingRecovery(false);
      } catch (error) {
        toast.error(error.data.message);
        setIsLoadingRecovery(false);
      }
    };
    fetchData();
  };

  const saveAffiliation = (formData: any): void => {
    const fetchData = async () => {
      try {
        setIsLoadingAffiliation(true);
        await agent.put(`${API_MYEDUZZ}/newproduct/${product.id}/affiliates/affiliation`, formData);
        toast.success('Informações de afiliação salvas com sucesso!');
        setContext(formData);
        setIsLoadingAffiliation(false);
      } catch (error) {
        toast.error(error.data.message);
        setIsLoadingAffiliation(false);
      }
    };
    fetchData();
  };

  const saveAffiliateInstructions = (formData: any): void => {
    const fetchData = async () => {
      try {
        setIsLoadingAffiliateInstructions(true);
        await agent.put(`${API_MYEDUZZ}/newproduct/${product.id}/affiliates/instructions`, formData);
        toast.success('Instruções salvas com sucesso!');
        setContext(formData);
        setIsLoadingAffiliateInstructions(false);
      } catch (error) {
        toast.error(error.data.message);
        setIsLoadingAffiliateInstructions(false);
      }
    };
    fetchData();
  };

  const saveRecruiter = (formData: any): void => {
    const fetchData = async () => {
      try {
        setIsLoadingRecruiter(true);
        await agent.put(`${API_MYEDUZZ}/newproduct/${product.id}/affiliates/recruitment`, formData);
        toast.success('Dados de recrutamento salvas com sucesso!');
        setContext(formData);
        setIsLoadingRecruiter(false);
      } catch (error) {
        toast.error(error.data.message);
        setIsLoadingRecruiter(false);
      }
    };
    fetchData();
  };

  const saveMaterials = (formData: any): void => {
    const fetchData = async () => {
      try {
        setIsLoadingMaterials(true);
        await agent.put(`${API_MYEDUZZ}/newproduct/${product.id}/affiliates/materials`, formData);
        toast.success('Material de divulgação salvo com sucesso!');
        setContext(formData);
        setIsLoadingMaterials(false);
      } catch (error) {
        toast.error(error.data.message);
        setIsLoadingMaterials(false);
      }
    };
    fetchData();
  };

  return {
    loadProduct,
    product,
    isLoading,

    basic,
    saveBasic,
    isLoadingBasic,
    setLoadingBasic,

    details,
    saveDetails,
    isLoadingDetails,
    setLoadingDetails,

    supportRefund,
    saveSupportRefund,
    isLoadingSupportRefund,
    setLoadingSupportRefund,

    paymentType,
    savePaymentType,
    isLoadingPaymentType,
    setIsLoadingPaymentType,

    checkout,
    saveCheckout,
    isLoadingCheckout,
    setIsLoadingCheckout,

    recovery,
    saveRecovery,
    isLoadingRecovery,
    setIsLoadingRecovery,

    affiliation,
    saveAffiliation,
    isLoadingAffiliation,
    setIsLoadingAffiliation,

    affiliateInstructions,
    saveAffiliateInstructions,
    isLoadingAffiliateInstructions,
    setIsLoadingAffiliateInstructions,

    recruiter,
    saveRecruiter,
    isLoadingRecruiter,
    setIsLoadingRecruiter,

    materials,
    isLoadingMaterials,
    setIsLoadingMaterials,
    saveMaterials,
  };
}
